/**
 * HOMS company-level module configuration (Sales OMS).
 * Same SaaS strategy as HPRMS: localStorage keyed by company + instance + HOMS.
 * Scope: standard HOMS admin routes only (not LOGISTIC / POS).
 */

import { MODULE_SETTINGS_PATH, getModuleConfigStorageKey } from "./moduleConfigurationUtils";
import { isSaudiArabiaCountry } from "./currencyUtils";

export { MODULE_SETTINGS_PATH };

export const HOMS_MODULE_KEYS = {
  DASHBOARD: "dashboard",
  SALES_PIPELINE: "sales_pipeline",
  QUOTATIONS: "quotations",
  SALES_ORDERS: "sales_orders",
  DELIVERIES: "deliveries",
  INVOICES: "invoices",
  CUSTOMERS: "customers",
  PRODUCTS: "products",
  CATEGORIES: "categories",
  INVENTORY: "inventory",
  REPORTS_SALES: "reports_sales",
  ZATCA: "zatca",
  DATA_WIZARD: "data_wizard",
  BUSINESS_SETTINGS: "business_settings",
  TAX_CONFIGURATION: "tax_configuration",
  PRINTER_SETTINGS: "printer_settings",
  MODULE_SETTINGS: "module_settings",
};

/** Nested pages (L2) under each module — Phase 1 definitions for Settings UI + future gate. */
export const HOMS_PAGE_DEFINITIONS_BY_MODULE = {
  [HOMS_MODULE_KEYS.DASHBOARD]: [],
  [HOMS_MODULE_KEYS.SALES_PIPELINE]: [],
  [HOMS_MODULE_KEYS.QUOTATIONS]: [
    {
      key: "quotations.new",
      label: "New quotation",
      match: "exact",
      paths: ["/Quotation/New"],
    },
    {
      key: "quotations.edit",
      label: "Edit quotation",
      match: "prefix",
      paths: ["/Quotation/Edit"],
    },
    {
      key: "quotations.detail",
      label: "Quotation detail",
      match: "detail",
      base: "/Quotation",
      excludePrefixes: ["/Quotation/New", "/Quotation/Edit"],
    },
  ],
  [HOMS_MODULE_KEYS.SALES_ORDERS]: [
    {
      key: "sales_orders.detail",
      label: "Order detail",
      match: "prefix",
      paths: ["/ViewOrder"],
    },
    {
      key: "sales_orders.refund",
      label: "Refund",
      match: "exact",
      paths: ["/refund"],
    },
  ],
  [HOMS_MODULE_KEYS.DELIVERIES]: [
    {
      key: "deliveries.new",
      label: "New delivery",
      match: "exact",
      paths: ["/Delivery/New"],
    },
    {
      key: "deliveries.detail",
      label: "Delivery detail",
      match: "detail",
      base: "/Delivery",
      excludePrefixes: ["/Delivery/New"],
    },
  ],
  [HOMS_MODULE_KEYS.INVOICES]: [
    {
      key: "invoices.new",
      label: "New invoice",
      match: "exact",
      paths: ["/Invoice/New"],
    },
    {
      key: "invoices.detail",
      label: "Invoice detail",
      match: "detail",
      base: "/Invoice",
      excludePrefixes: ["/Invoice/New"],
    },
  ],
  [HOMS_MODULE_KEYS.CUSTOMERS]: [
    {
      key: "customers.add",
      label: "Add customer",
      match: "exact",
      paths: ["/customer"],
    },
    {
      key: "customers.edit",
      label: "Edit customer",
      match: "prefix",
      paths: ["/editcustomer"],
    },
  ],
  [HOMS_MODULE_KEYS.PRODUCTS]: [
    {
      key: "products.create",
      label: "Add product",
      match: "exact",
      paths: ["/ProductForm"],
    },
    {
      key: "products.edit",
      label: "Edit product",
      match: "prefix",
      paths: ["/editproduct"],
    },
  ],
  [HOMS_MODULE_KEYS.CATEGORIES]: [
    {
      key: "categories.add",
      label: "Add category",
      match: "prefix",
      paths: ["/Category/Add", "/Category"],
    },
  ],
  [HOMS_MODULE_KEYS.INVENTORY]: [
    {
      key: "inventory.history",
      label: "Inventory history",
      match: "prefix",
      paths: ["/inventoryHistory", "/inventryHistory"],
    },
  ],
  [HOMS_MODULE_KEYS.REPORTS_SALES]: [],
  [HOMS_MODULE_KEYS.ZATCA]: [],
  [HOMS_MODULE_KEYS.DATA_WIZARD]: [],
  [HOMS_MODULE_KEYS.BUSINESS_SETTINGS]: [],
  [HOMS_MODULE_KEYS.TAX_CONFIGURATION]: [],
  [HOMS_MODULE_KEYS.PRINTER_SETTINGS]: [],
  [HOMS_MODULE_KEYS.MODULE_SETTINGS]: [],
};

/** Optional field groups (L3). Defaults ON. OFF = hide on create/edit UI; existing values kept on save. */
export const HOMS_FIELD_GROUP_KEYS = {
  PRODUCTS_TAX_DISCOUNT: "products.tax_discount",
  PRODUCTS_EXTRA_ATTRS: "products.extra_attrs",
  PRODUCTS_IMAGES: "products.images",
  INVENTORY_LIST_ACTIONS: "inventory.list_actions",
  CUSTOMERS_ADDRESS: "customers.address",
};

export const HOMS_FIELD_GROUP_DEFINITIONS_BY_MODULE = {
  [HOMS_MODULE_KEYS.PRODUCTS]: [
    {
      key: HOMS_FIELD_GROUP_KEYS.PRODUCTS_TAX_DISCOUNT,
      label: "Tax % / Discount %",
      description: "Optional pricing fields on create / edit product",
    },
    {
      key: HOMS_FIELD_GROUP_KEYS.PRODUCTS_EXTRA_ATTRS,
      label: "Extra attributes",
      description: "Manufacturer, Supplier, SKU, Expiry date",
    },
    {
      key: HOMS_FIELD_GROUP_KEYS.PRODUCTS_IMAGES,
      label: "Product images",
      description: "Image upload on create / edit product",
    },
  ],
  [HOMS_MODULE_KEYS.INVENTORY]: [
    {
      key: HOMS_FIELD_GROUP_KEYS.INVENTORY_LIST_ACTIONS,
      label: "Actions column",
      description: "Add / adjust inventory on the Inventory list",
    },
  ],
  [HOMS_MODULE_KEYS.CUSTOMERS]: [
    {
      key: HOMS_FIELD_GROUP_KEYS.CUSTOMERS_ADDRESS,
      label: "Customer address",
      description: "Street, city, state, post code, country (name / email / phone stay)",
    },
  ],
};

export const HOMS_MODULE_DEFINITIONS = [
  {
    key: HOMS_MODULE_KEYS.DASHBOARD,
    label: "Dashboard",
    section: "DASHBOARD",
    locked: true,
    summary: "Company home / order operations overview.",
  },
  {
    key: HOMS_MODULE_KEYS.SALES_PIPELINE,
    label: "Sales Pipeline",
    section: "SALES & ORDERS",
    summary: "Kanban-style sales stages.",
  },
  {
    key: HOMS_MODULE_KEYS.QUOTATIONS,
    label: "Quotations",
    section: "SALES & ORDERS",
    summary: "Create and manage customer quotations.",
  },
  {
    key: HOMS_MODULE_KEYS.SALES_ORDERS,
    label: "Sales Orders",
    section: "SALES & ORDERS",
    summary: "Sales order list, detail, and refunds.",
  },
  {
    key: HOMS_MODULE_KEYS.DELIVERIES,
    label: "Deliveries",
    section: "SALES & ORDERS",
    summary: "Deliver goods against sales orders.",
  },
  {
    key: HOMS_MODULE_KEYS.INVOICES,
    label: "Invoices",
    section: "SALES & ORDERS",
    summary: "Customer invoices from orders / deliveries.",
  },
  {
    key: HOMS_MODULE_KEYS.CUSTOMERS,
    label: "Customers",
    section: "SALES & ORDERS",
    summary: "Customer list and profiles.",
  },
  {
    key: HOMS_MODULE_KEYS.PRODUCTS,
    label: "Products",
    section: "PRODUCT CATALOG",
    summary: "Sellable product catalogue.",
  },
  {
    key: HOMS_MODULE_KEYS.CATEGORIES,
    label: "Categories",
    section: "PRODUCT CATALOG",
    summary: "Product categories.",
  },
  {
    key: HOMS_MODULE_KEYS.INVENTORY,
    label: "Inventory",
    section: "STOCK OPERATIONS",
    summary: "On-hand stock and history.",
  },
  {
    key: HOMS_MODULE_KEYS.REPORTS_SALES,
    label: "Sales reports",
    section: "REPORTS",
    summary: "Sales overview, top products, top customers.",
  },
  {
    key: HOMS_MODULE_KEYS.ZATCA,
    label: "ZATCA invoices",
    section: "REPORTS",
    summary: "ZATCA invoice list / clearance (Saudi Arabia companies only).",
  },
  {
    key: HOMS_MODULE_KEYS.DATA_WIZARD,
    label: "Data Import/Export",
    section: "SETTINGS",
    summary: "Bulk import and export.",
  },
  {
    key: HOMS_MODULE_KEYS.BUSINESS_SETTINGS,
    label: "Business Settings",
    section: "SETTINGS",
    summary: "Company receipt / business metadata (Hom setup).",
  },
  {
    key: HOMS_MODULE_KEYS.TAX_CONFIGURATION,
    label: "Tax Configuration",
    section: "SETTINGS",
    summary: "Tax / ZATCA configuration (Saudi Arabia companies only).",
  },
  {
    key: HOMS_MODULE_KEYS.PRINTER_SETTINGS,
    label: "Printer Server Settings",
    section: "SETTINGS",
    summary: "Printer server URL (Hom setup).",
  },
  {
    key: HOMS_MODULE_KEYS.MODULE_SETTINGS,
    label: "Module Configuration",
    section: "SETTINGS",
    locked: true,
    summary: "This settings page — always available.",
  },
];

export const HOMS_SECTION_HELP = {
  DASHBOARD: "Home screen for this company.",
  "SALES & ORDERS": "Quote → order → deliver → invoice journey.",
  "PRODUCT CATALOG": "What you sell.",
  "STOCK OPERATIONS": "Stock on hand.",
  REPORTS: "Sales analytics and ZATCA.",
  SETTINGS: "Company setup and imports.",
};

/** Longest-prefix wins for path → module. */
export const HOMS_PATH_MODULE_RULES = [
  { prefix: MODULE_SETTINGS_PATH, moduleKey: HOMS_MODULE_KEYS.MODULE_SETTINGS },
  { prefix: "/configuration/data-wizard", moduleKey: HOMS_MODULE_KEYS.DATA_WIZARD },
  { prefix: "/configuration/business-settings", moduleKey: HOMS_MODULE_KEYS.BUSINESS_SETTINGS },
  { prefix: "/configuration/tax-configuration", moduleKey: HOMS_MODULE_KEYS.TAX_CONFIGURATION },
  { prefix: "/configuration/printer-settings", moduleKey: HOMS_MODULE_KEYS.PRINTER_SETTINGS },
  { prefix: "/zatca/invoices", moduleKey: HOMS_MODULE_KEYS.ZATCA },
  { prefix: "/reports/dashboard", moduleKey: HOMS_MODULE_KEYS.DASHBOARD },
  { prefix: "/reports/sales", moduleKey: HOMS_MODULE_KEYS.REPORTS_SALES },
  { prefix: "/SalesPipeline", moduleKey: HOMS_MODULE_KEYS.SALES_PIPELINE },
  { prefix: "/Quotations", moduleKey: HOMS_MODULE_KEYS.QUOTATIONS },
  { prefix: "/Quotation", moduleKey: HOMS_MODULE_KEYS.QUOTATIONS },
  { prefix: "/Deliveries", moduleKey: HOMS_MODULE_KEYS.DELIVERIES },
  { prefix: "/Delivery", moduleKey: HOMS_MODULE_KEYS.DELIVERIES },
  { prefix: "/Invoices", moduleKey: HOMS_MODULE_KEYS.INVOICES },
  { prefix: "/Invoice", moduleKey: HOMS_MODULE_KEYS.INVOICES },
  { prefix: "/Sales Order", moduleKey: HOMS_MODULE_KEYS.SALES_ORDERS },
  { prefix: "/ViewOrders", moduleKey: HOMS_MODULE_KEYS.SALES_ORDERS },
  { prefix: "/ViewOrder", moduleKey: HOMS_MODULE_KEYS.SALES_ORDERS },
  { prefix: "/refund", moduleKey: HOMS_MODULE_KEYS.SALES_ORDERS },
  { prefix: "/Customers", moduleKey: HOMS_MODULE_KEYS.CUSTOMERS },
  { prefix: "/customer", moduleKey: HOMS_MODULE_KEYS.CUSTOMERS },
  { prefix: "/editcustomer", moduleKey: HOMS_MODULE_KEYS.CUSTOMERS },
  { prefix: "/Products", moduleKey: HOMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/ProductForm", moduleKey: HOMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/editproduct", moduleKey: HOMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/Categories", moduleKey: HOMS_MODULE_KEYS.CATEGORIES },
  { prefix: "/Category", moduleKey: HOMS_MODULE_KEYS.CATEGORIES },
  { prefix: "/inventryHistory", moduleKey: HOMS_MODULE_KEYS.INVENTORY },
  { prefix: "/inventoryHistory", moduleKey: HOMS_MODULE_KEYS.INVENTORY },
  { prefix: "/inventory", moduleKey: HOMS_MODULE_KEYS.INVENTORY },
];

export const HOMS_MODULE_DEPENDENCIES = [
  {
    moduleKey: HOMS_MODULE_KEYS.PRODUCTS,
    requires: [HOMS_MODULE_KEYS.CATEGORIES],
    reason: "Products use Categories to organize the catalogue.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.QUOTATIONS,
    requires: [HOMS_MODULE_KEYS.PRODUCTS, HOMS_MODULE_KEYS.CUSTOMERS],
    reason: "Quotations need Products on quote lines and a Customer.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.SALES_ORDERS,
    requires: [HOMS_MODULE_KEYS.PRODUCTS],
    reason: "Sales Orders need Products on order lines.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.DELIVERIES,
    requires: [HOMS_MODULE_KEYS.SALES_ORDERS],
    reason: "Deliveries ship against Sales Orders.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.INVOICES,
    requires: [HOMS_MODULE_KEYS.SALES_ORDERS],
    reason: "Invoices are created from Sales Orders.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.INVENTORY,
    requires: [HOMS_MODULE_KEYS.PRODUCTS],
    reason: "Inventory tracks Products.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.ZATCA,
    requires: [HOMS_MODULE_KEYS.INVOICES],
    reason: "ZATCA list is based on Invoices.",
  },
  {
    moduleKey: HOMS_MODULE_KEYS.REPORTS_SALES,
    requires: [HOMS_MODULE_KEYS.SALES_ORDERS],
    reason: "Sales reports use order data.",
  },
];

/** Page (L2) links: when pageKey is ON, every key in requires must stay ON. */
export const HOMS_PAGE_DEPENDENCIES = [
  {
    pageKey: "products.create",
    requires: ["categories.add"],
    reason: "Creating a product needs Add category.",
  },
];

export const HOMS_MODULE_PACKS = [
  {
    id: "simple_sales",
    label: "Simple sales",
    description: "Orders, customers, products, inventory — no quote/delivery/invoice/reports",
    modules: {
      [HOMS_MODULE_KEYS.DASHBOARD]: true,
      [HOMS_MODULE_KEYS.SALES_PIPELINE]: false,
      [HOMS_MODULE_KEYS.QUOTATIONS]: false,
      [HOMS_MODULE_KEYS.SALES_ORDERS]: true,
      [HOMS_MODULE_KEYS.DELIVERIES]: false,
      [HOMS_MODULE_KEYS.INVOICES]: false,
      [HOMS_MODULE_KEYS.CUSTOMERS]: true,
      [HOMS_MODULE_KEYS.PRODUCTS]: true,
      [HOMS_MODULE_KEYS.CATEGORIES]: true,
      [HOMS_MODULE_KEYS.INVENTORY]: true,
      [HOMS_MODULE_KEYS.REPORTS_SALES]: false,
      [HOMS_MODULE_KEYS.ZATCA]: false,
      // Settings modules are not touched — keep whatever the company already has
    },
  },
  {
    id: "full_sales_journey",
    label: "Full sales journey",
    description: "Pipeline → quotation → order → delivery → invoice — reports/ZATCA off by default",
    modules: {
      [HOMS_MODULE_KEYS.DASHBOARD]: true,
      [HOMS_MODULE_KEYS.SALES_PIPELINE]: true,
      [HOMS_MODULE_KEYS.QUOTATIONS]: true,
      [HOMS_MODULE_KEYS.SALES_ORDERS]: true,
      [HOMS_MODULE_KEYS.DELIVERIES]: true,
      [HOMS_MODULE_KEYS.INVOICES]: true,
      [HOMS_MODULE_KEYS.CUSTOMERS]: true,
      [HOMS_MODULE_KEYS.PRODUCTS]: true,
      [HOMS_MODULE_KEYS.CATEGORIES]: true,
      [HOMS_MODULE_KEYS.INVENTORY]: true,
      [HOMS_MODULE_KEYS.REPORTS_SALES]: false,
      [HOMS_MODULE_KEYS.ZATCA]: false,
      // Settings modules are not touched — keep whatever the company already has
    },
  },
  {
    id: "catalog_only",
    label: "Catalog only",
    description: "Products, categories, inventory only (settings off except Module Configuration)",
    modules: {
      [HOMS_MODULE_KEYS.DASHBOARD]: true,
      [HOMS_MODULE_KEYS.SALES_PIPELINE]: false,
      [HOMS_MODULE_KEYS.QUOTATIONS]: false,
      [HOMS_MODULE_KEYS.SALES_ORDERS]: false,
      [HOMS_MODULE_KEYS.DELIVERIES]: false,
      [HOMS_MODULE_KEYS.INVOICES]: false,
      [HOMS_MODULE_KEYS.CUSTOMERS]: false,
      [HOMS_MODULE_KEYS.PRODUCTS]: true,
      [HOMS_MODULE_KEYS.CATEGORIES]: true,
      [HOMS_MODULE_KEYS.INVENTORY]: true,
      [HOMS_MODULE_KEYS.REPORTS_SALES]: false,
      [HOMS_MODULE_KEYS.ZATCA]: false,
      [HOMS_MODULE_KEYS.DATA_WIZARD]: false,
      [HOMS_MODULE_KEYS.BUSINESS_SETTINGS]: false,
      [HOMS_MODULE_KEYS.TAX_CONFIGURATION]: false,
      [HOMS_MODULE_KEYS.PRINTER_SETTINGS]: false,
      [HOMS_MODULE_KEYS.MODULE_SETTINGS]: true,
    },
  },
];

function getAllPageDefinitions() {
  return Object.values(HOMS_PAGE_DEFINITIONS_BY_MODULE).flat();
}

function getAllFieldGroupDefinitions() {
  return Object.values(HOMS_FIELD_GROUP_DEFINITIONS_BY_MODULE).flat();
}

export function getDefaultModules() {
  const modules = {};
  HOMS_MODULE_DEFINITIONS.forEach(({ key }) => {
    modules[key] = true;
  });
  modules[HOMS_MODULE_KEYS.DASHBOARD] = true;
  modules[HOMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return modules;
}

export function getDefaultPages() {
  const pages = {};
  getAllPageDefinitions().forEach((page) => {
    pages[page.key] = true;
  });
  return pages;
}

export function getDefaultFieldGroups() {
  const fieldGroups = {};
  getAllFieldGroupDefinitions().forEach((group) => {
    fieldGroups[group.key] = true;
  });
  return fieldGroups;
}

export function getDefaultModuleConfiguration(meta = {}) {
  return {
    companyName: meta.companyName || "",
    instanceId: meta.instanceId || "",
    appType: "HOMS",
    modules: getDefaultModules(),
    pages: getDefaultPages(),
    fieldGroups: getDefaultFieldGroups(),
    updatedAt: null,
  };
}

function moduleLabel(key) {
  const def = HOMS_MODULE_DEFINITIONS.find((d) => d.key === key);
  return def?.label || key;
}

export function enforceModuleDependencies(modules) {
  const next = { ...(modules || getDefaultModules()) };
  const enabled = [];
  let changed = true;
  while (changed) {
    changed = false;
    HOMS_MODULE_DEPENDENCIES.forEach((rule) => {
      if (next[rule.moduleKey] === false) return;
      rule.requires.forEach((req) => {
        if (next[req] === false) {
          next[req] = true;
          enabled.push(moduleLabel(req));
          changed = true;
        }
      });
    });
  }
  next[HOMS_MODULE_KEYS.DASHBOARD] = true;
  next[HOMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return {
    modules: next,
    autoEnabledLabels: [...new Set(enabled)],
  };
}

export function normalizeModuleConfiguration(config, meta = {}) {
  const defaults = getDefaultModuleConfiguration(meta);
  if (!config || typeof config !== "object") return defaults;

  const modules = { ...defaults.modules };
  const incomingModules =
    config.modules && typeof config.modules === "object" ? config.modules : {};
  Object.keys(modules).forEach((key) => {
    if (typeof incomingModules[key] === "boolean") {
      modules[key] = incomingModules[key];
    }
  });
  modules[HOMS_MODULE_KEYS.DASHBOARD] = true;
  modules[HOMS_MODULE_KEYS.MODULE_SETTINGS] = true;

  const pages = { ...defaults.pages };
  const incomingPages = config.pages && typeof config.pages === "object" ? config.pages : {};
  Object.keys(pages).forEach((key) => {
    if (typeof incomingPages[key] === "boolean") {
      pages[key] = incomingPages[key];
    }
  });

  const fieldGroups = { ...defaults.fieldGroups };
  const incomingFieldGroups =
    config.fieldGroups && typeof config.fieldGroups === "object" ? config.fieldGroups : {};
  Object.keys(fieldGroups).forEach((key) => {
    if (typeof incomingFieldGroups[key] === "boolean") {
      fieldGroups[key] = incomingFieldGroups[key];
    }
  });

  const enforced = enforceModuleDependencies(modules);
  const enforcedPages = enforcePageDependencies(pages);

  return {
    companyName: config.companyName || meta.companyName || defaults.companyName,
    instanceId: config.instanceId || meta.instanceId || defaults.instanceId,
    appType: "HOMS",
    modules: enforced.modules,
    pages: enforcedPages.pages,
    fieldGroups,
    updatedAt: config.updatedAt || null,
  };
}

export function loadModuleConfiguration(companyName, instanceId, appType = "HOMS") {
  const meta = { companyName, instanceId, appType };
  if (!companyName || !instanceId) {
    return getDefaultModuleConfiguration(meta);
  }
  try {
    const raw = localStorage.getItem(getModuleConfigStorageKey(companyName, instanceId, appType));
    if (!raw) return getDefaultModuleConfiguration(meta);
    return normalizeModuleConfiguration(JSON.parse(raw), meta);
  } catch {
    return getDefaultModuleConfiguration(meta);
  }
}

export function saveModuleConfiguration(companyName, instanceId, config, appType = "HOMS") {
  const normalized = normalizeModuleConfiguration(config, { companyName, instanceId, appType });
  const toStore = {
    ...normalized,
    companyName,
    instanceId,
    appType: "HOMS",
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(
    getModuleConfigStorageKey(companyName, instanceId, appType),
    JSON.stringify(toStore)
  );
  return toStore;
}

export function getPagesForModule(moduleKey) {
  return HOMS_PAGE_DEFINITIONS_BY_MODULE[moduleKey] || [];
}

export function getFieldGroupsForModule(moduleKey) {
  return HOMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[moduleKey] || [];
}

export function moduleHasConfigurableDetails(moduleKey) {
  return (
    getPagesForModule(moduleKey).length > 0 || getFieldGroupsForModule(moduleKey).length > 0
  );
}

export function isHomsAppType(appType) {
  if (!appType) return false;
  return String(appType).trim().replace(/-+$/, "").toUpperCase() === "HOMS";
}

export function getBlockingDependents(moduleKey, modules) {
  if (!moduleKey || !modules) return [];
  return HOMS_MODULE_DEPENDENCIES.filter(
    (rule) =>
      rule.requires.includes(moduleKey) && modules[rule.moduleKey] !== false
  ).map((rule) => ({
    key: rule.moduleKey,
    label: moduleLabel(rule.moduleKey),
    reason: rule.reason,
  }));
}

export function getTurnOffBlockMessage(moduleKey, modules) {
  const blockers = getBlockingDependents(moduleKey, modules);
  if (!blockers.length) return "";
  const names = blockers.map((b) => b.label).join(", ");
  return `${moduleLabel(moduleKey)} stays on while ${names} is enabled. ${blockers[0].reason} Turn off ${names} first if you want to disable this.`;
}

export function applyModulePack(packId, currentModules = {}) {
  const pack = HOMS_MODULE_PACKS.find((p) => p.id === packId);
  if (!pack) return { ...currentModules };
  const next = {
    ...getDefaultModules(),
    ...currentModules,
    ...pack.modules,
  };
  next[HOMS_MODULE_KEYS.DASHBOARD] = true;
  next[HOMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return enforceModuleDependencies(next).modules;
}

export function tryToggleModule(modules, moduleKey, enable) {
  const current = { ...(modules || getDefaultModules()) };
  if (
    moduleKey === HOMS_MODULE_KEYS.DASHBOARD ||
    moduleKey === HOMS_MODULE_KEYS.MODULE_SETTINGS
  ) {
    return { ok: false, modules: current, message: "" };
  }

  if (!enable) {
    const blockers = getBlockingDependents(moduleKey, current);
    if (blockers.length) {
      return {
        ok: false,
        modules: current,
        message: getTurnOffBlockMessage(moduleKey, current),
      };
    }
    current[moduleKey] = false;
    return { ok: true, modules: current, message: "" };
  }

  current[moduleKey] = true;
  const enforced = enforceModuleDependencies(current);
  const labels = enforced.autoEnabledLabels || [];
  return {
    ok: true,
    modules: enforced.modules,
    message: labels.length
      ? `Also turned on: ${labels.join(", ")} (required).`
      : "",
  };
}

function pageLabel(pageKey) {
  const page = getAllPageDefinitions().find((p) => p.key === pageKey);
  return page?.label || pageKey;
}

export function enforcePageDependencies(pages) {
  const next = { ...(pages || getDefaultPages()) };
  const enabled = [];
  let changed = true;
  while (changed) {
    changed = false;
    HOMS_PAGE_DEPENDENCIES.forEach((rule) => {
      if (next[rule.pageKey] === false) return;
      rule.requires.forEach((req) => {
        if (next[req] === false) {
          next[req] = true;
          enabled.push(pageLabel(req));
          changed = true;
        }
      });
    });
  }
  return {
    pages: next,
    autoEnabledLabels: [...new Set(enabled)],
  };
}

export function getBlockingPageDependents(pageKey, pages) {
  if (!pageKey || !pages) return [];
  return HOMS_PAGE_DEPENDENCIES.filter(
    (rule) =>
      rule.requires.includes(pageKey) && pages[rule.pageKey] !== false
  ).map((rule) => ({
    key: rule.pageKey,
    label: pageLabel(rule.pageKey),
    reason: rule.reason,
  }));
}

export function getTurnOffPageBlockMessage(pageKey, pages) {
  const blockers = getBlockingPageDependents(pageKey, pages);
  if (!blockers.length) return "";
  const names = blockers.map((b) => b.label).join(", ");
  return `${pageLabel(pageKey)} stays on while ${names} is enabled. ${blockers[0].reason} Turn off ${names} first if you want to disable this.`;
}

export function tryTogglePage(pages, pageKey, enable) {
  const current = { ...(pages || getDefaultPages()) };
  if (!pageKey) return { ok: false, pages: current, message: "" };

  if (!enable) {
    const blockers = getBlockingPageDependents(pageKey, current);
    if (blockers.length) {
      return {
        ok: false,
        pages: current,
        message: getTurnOffPageBlockMessage(pageKey, current),
      };
    }
    current[pageKey] = false;
    return { ok: true, pages: current, message: "" };
  }

  current[pageKey] = true;
  const enforced = enforcePageDependencies(current);
  const labels = enforced.autoEnabledLabels || [];
  return {
    ok: true,
    pages: enforced.pages,
    message: labels.length
      ? `Also turned on: ${labels.join(", ")} (required).`
      : "",
  };
}

export function isModuleEnabled(modules, moduleKey) {
  if (!moduleKey) return true;
  if (
    moduleKey === HOMS_MODULE_KEYS.MODULE_SETTINGS ||
    moduleKey === HOMS_MODULE_KEYS.DASHBOARD
  ) {
    return true;
  }
  if (!modules || typeof modules !== "object") return true;
  return modules[moduleKey] !== false;
}

/**
 * ZATCA + Tax Configuration are only meaningful for Saudi Arabia companies.
 * Non-Saudi → always force both OFF.
 */
export function enforceZatcaByCountry(modules, country) {
  const next = { ...(modules || getDefaultModules()) };
  if (!isSaudiArabiaCountry(country)) {
    next[HOMS_MODULE_KEYS.ZATCA] = false;
    next[HOMS_MODULE_KEYS.TAX_CONFIGURATION] = false;
  }
  return next;
}

/** ZATCA / Tax Configuration module toggles are allowed only for Saudi Arabia. */
export function isZatcaToggleAllowed(country) {
  return isSaudiArabiaCountry(country);
}

export function isTaxConfigurationToggleAllowed(country) {
  return isSaudiArabiaCountry(country);
}

export function isPageEnabled(pages, pageKey) {
  if (!pageKey) return true;
  if (!pages || typeof pages !== "object") return true;
  return pages[pageKey] !== false;
}

export function isFieldGroupEnabled(fieldGroups, groupKey) {
  if (!groupKey) return true;
  if (!fieldGroups || typeof fieldGroups !== "object") return true;
  return fieldGroups[groupKey] !== false;
}

function pathMatchesPageRule(path, rule) {
  if (!rule) return false;

  if (rule.match === "exact") {
    return (rule.paths || []).some((p) => path === p);
  }

  if (rule.match === "prefix") {
    // Exact or segment boundary only — avoid `/ViewOrder` matching `/ViewOrders`.
    return (rule.paths || []).some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
  }

  if (rule.match === "suffix") {
    const okSuffix = (rule.paths || []).some((p) => path.endsWith(p));
    if (!okSuffix) return false;
    if (rule.mustInclude && !path.includes(rule.mustInclude)) return false;
    return true;
  }

  if (rule.match === "detail") {
    const base = rule.base;
    if (!base) return false;
    if (path === base || path === `${base}s`) return false;
    if (!(path === base || path.startsWith(`${base}/`))) return false;
    const excluded = (rule.excludePrefixes || []).some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (excluded) return false;
    const excludedSuffix = (rule.excludeSuffixes || []).some((s) => path.endsWith(s));
    if (excludedSuffix) return false;
    return true;
  }

  return false;
}

export function getPageKeyForPath(pathname) {
  if (!pathname) return null;
  const path = pathname.split("?")[0];
  const allPages = getAllPageDefinitions();

  for (const rule of allPages) {
    if (pathMatchesPageRule(path, rule)) {
      return rule.key;
    }
  }
  return null;
}

export function getModuleKeyForPath(pathname) {
  if (!pathname) return null;
  const path = pathname.split("?")[0];
  const rules = [...HOMS_PATH_MODULE_RULES].sort(
    (a, b) => b.prefix.length - a.prefix.length
  );

  for (const rule of rules) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      return rule.moduleKey;
    }
    if (path.startsWith(rule.prefix) && rule.prefix.includes(" ")) {
      return rule.moduleKey;
    }
  }
  return null;
}

export function isHomsPathEnabled(pathname, modules, pages) {
  const moduleKey = getModuleKeyForPath(pathname);
  if (moduleKey && !isModuleEnabled(modules, moduleKey)) {
    return false;
  }

  const pageKey = getPageKeyForPath(pathname);
  if (pageKey && !isPageEnabled(pages, pageKey)) {
    return false;
  }

  return true;
}

export function getHomsFallbackPath(modules) {
  if (isModuleEnabled(modules, HOMS_MODULE_KEYS.DASHBOARD)) {
    return "/reports/dashboard";
  }
  return MODULE_SETTINGS_PATH;
}

/** Filter sidebar sections for HOMS using module + page flags. */
export function filterHomsMenuSections(sections, modules, pages) {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => {
        if (item.action) return true;
        if (!item.path) return true;
        return isHomsPathEnabled(item.path, modules, pages);
      }),
    }))
    .filter((section) => section.items.length > 0);
}
